// Stages across, time down. That is the layout the screenshots people already pass
// around every year use, so it is the one they read without being taught.
//
// A member's own blocks are emphasized and everyone else's are ambient markers on
// the same block, which is what makes a group view legible rather than six
// schedules stacked.

import { useEffect, useState } from 'react';
import type { Artist, DayContext, Id, Member, PlanBlock } from '../../engine/types.ts';
import { fmtTime } from '../../engine/time.ts';
import { readSettings, stageColor, stageColor2 } from '../theme.ts';
import type { CSSProperties } from 'react';

const HOUR = 60 * 60 * 1000;
const FALLBACK_PX_PER_HOUR = 84;

/** The viewport width, so the column geometry can answer to the screen it is on. */
function useViewportWidth(): number {
  const [width, setWidth] = useState(() =>
    typeof window === 'undefined' ? 1200 : window.innerWidth,
  );
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);
  return width;
}

// How wide a stage column and the time gutter beside it are drawn. The widths
// are picked so two columns plus the gutter fit inside the narrowest phone, which
// makes the sideways scroll a way of reaching the other stages rather than the
// only way to read any of them.
function geometry(viewport: number): { col: number; gutter: number } {
  if (viewport <= 420) return { col: 132, gutter: 44 };
  if (viewport <= 700) return { col: 152, gutter: 52 };
  if (viewport <= 900) return { col: 168, gutter: 58 };
  return { col: 188, gutter: 64 };
}

// The largest size at which the whole name still fits the block with no word
// broken part way. Width is measured against the column it will be drawn in.
function fitName(name: string, boxHeight: number, showWhen: boolean, colWidth: number): number {
  const nameWidth = colWidth - 24;
  const words = name.split(/\s+/).filter(Boolean);
  const longest = Math.max(1, ...words.map((w) => w.length));
  const room = Math.max(14, boxHeight - (showWhen ? 30 : 10));
  for (let size = boxHeight >= 100 ? 34 : 28; size > 11; size -= 1) {
    const perLine = Math.floor(nameWidth / (size * 0.64));
    if (perLine < longest) continue;
    let lines = 1;
    let used = 0;
    for (const word of words) {
      const need = used === 0 ? word.length : used + 1 + word.length;
      if (need <= perLine) used = need;
      else {
        lines += 1;
        used = word.length;
      }
    }
    if (lines * size * 1.14 <= room) return size;
  }
  return 11;
}

export interface StageGridProps {
  ctx: DayContext;
  blocks: PlanBlock[];
  artistById: Map<Id, Artist>;
  hardLockSetIds?: Id[];
  presence?: Map<Id, Member[]>;
  onSetClick?: (setId: Id) => void;
  /** Pixels per hour. Defaults to the density chosen in settings. */
  hourHeight?: number;
  /** Sets the person put in their own plan. Drives the highlight when supplied. */
  pickedSetIds?: Set<Id>;
}

export function StageGrid({
  ctx,
  blocks,
  artistById,
  hardLockSetIds = [],
  presence,
  onSetClick,
  hourHeight,
  pickedSetIds,
}: StageGridProps) {
  // The settings screen offers an hour height and the geometry ignored it, so
  // the control moved a CSS variable nothing read. One value drives the ticks,
  // the column height and every block.
  const viewport = useViewportWidth();
  const { col: COL_MIN, gutter: GUTTER } = geometry(viewport);

  // Which stages the reader has chosen to look at. Empty means all of them, so a
  // day whose stages change does not leave a stale selection behind.
  const [only, setOnly] = useState<Id[]>([]);

  const saved = readSettings().hourHeight;
  const PX_PER_HOUR =
    hourHeight ?? (Number.isFinite(saved) && saved >= 40 && saved <= 200 ? saved : FALLBACK_PX_PER_HOUR);
  // Only the stages running that day. Grove runs Wednesday and Thursday, Raptor
  // Alley runs late, and an empty column is a column nobody needs to scroll past.
  const playing = new Set(ctx.sets.map((s) => s.stageId));
  const running = ctx.stages.filter((s) => !s.isNonMusic && playing.has(s.id));
  const chosen = only.filter((id) => running.some((s) => s.id === id));
  const stages = chosen.length > 0 ? running.filter((s) => chosen.includes(s.id)) : running;

  const musicSets = ctx.sets;
  if (musicSets.length === 0) {
    return <p className="muted">No sets on this day yet. Paste a grid on the import screen.</p>;
  }

  // The chooser appears only when the stages running cannot fit the screen. On a
  // desktop they do, and a control that does nothing is worse than no control.
  const fits = GUTTER + running.length * COL_MIN <= viewport - 40;
  const toggle = (id: Id) =>
    setOnly((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const first = Math.min(...musicSets.map((s) => s.startTs));
  const last = Math.max(...musicSets.map((s) => s.endTs));
  const start = Math.floor(first / HOUR) * HOUR;
  const end = Math.ceil(last / HOUR) * HOUR;
  const hours = Math.max(1, Math.round((end - start) / HOUR));
  const height = hours * PX_PER_HOUR;

  const attendedById = new Map<Id, PlanBlock>();
  for (const b of blocks) if (b.setId) attendedById.set(b.setId, b);

  const columns = `${GUTTER}px repeat(${stages.length}, minmax(${COL_MIN}px, 1fr))`;
  // Six columns at a readable width do not fit half a desktop, so the grid
  // keeps its width and the wrapper scrolls sideways.
  const minWidth = GUTTER + stages.length * COL_MIN;
  const top = (ts: number) => ((ts - start) / HOUR) * PX_PER_HOUR;

  return (
    <>
      {!fits && (
        <div className="stagepicker">
          <button className={`chip ${chosen.length === 0 ? 'on' : ''}`} onClick={() => setOnly([])}>
            All {running.length}
          </button>
          {running.map((s) => (
            <button
              key={s.id}
              className={`chip ${chosen.includes(s.id) ? 'on' : ''}`}
              style={{ '--stage': stageColor(s.id) } as CSSProperties}
              onClick={() => toggle(s.id)}
            >
              {s.name}
            </button>
          ))}
        </div>
      )}
    <div className="grid-wrap" style={{ ['--gutter' as string]: `${GUTTER}px` }}>
      <div className="grid" style={{ gridTemplateColumns: '1fr', minWidth }}>
        <div className="grid-head" style={{ gridTemplateColumns: columns }}>
          <div>Time</div>
          {stages.map((s) => (
            <div key={s.id} style={{ '--stage': stageColor(s.id), '--stage2': stageColor2(s.id) } as CSSProperties}>
              {s.name}
            </div>
          ))}
        </div>
        <div className="grid-body" style={{ gridTemplateColumns: columns, height }}>
          <div className="grid-times">
            {Array.from({ length: hours + 1 }, (_, i) => (
              <div key={i} className="grid-time" style={{ top: i * PX_PER_HOUR }}>
                {fmtTime(start + i * HOUR)}
              </div>
            ))}
          </div>
          {stages.map((stage) => (
            <div
              key={stage.id}
              className="grid-col"
              style={{ '--stage': stageColor(stage.id), '--stage2': stageColor2(stage.id) } as CSSProperties}
            >
              {Array.from({ length: hours + 1 }, (_, i) => (
                <div key={i} className="grid-rule" style={{ top: i * PX_PER_HOUR }} />
              ))}
              {ctx.sets
                .filter((s) => s.stageId === stage.id)
                .map((set) => {
                  const block = attendedById.get(set.id);
                  const locked = hardLockSetIds.includes(set.id);
                  const full = block?.mode === 'full';
                  const partial = block && !full;
                  const here = presence?.get(set.id) ?? [];
                  const picked = pickedSetIds?.has(set.id) ?? false;
                  const classes = [
                    'set',
                    locked ? 'locked' : full ? 'planned' : partial ? 'partial' : '',
                  ]
                    .filter(Boolean)
                    .join(' ');
                  const boxHeight = Math.max(20, ((set.endTs - set.startTs) / HOUR) * PX_PER_HOUR - 3);
                  const name = artistById.get(set.artistId)?.name ?? set.artistId;
                  const showWhen = boxHeight >= 46;
                  const nameSize = fitName(name, boxHeight, showWhen, COL_MIN);
                  return (
                    <div
                      key={set.id}
                      className={classes}
                      style={{ top: top(set.startTs), height: boxHeight }}
                      role={onSetClick ? 'button' : undefined}
                      tabIndex={onSetClick ? 0 : undefined}
                      aria-pressed={pickedSetIds ? picked : undefined}
                      onClick={() => onSetClick?.(set.id)}
                      onKeyDown={(e) => {
                        if (!onSetClick) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onSetClick(set.id);
                        }
                      }}
                      title={`${name} at ${stage.name}${set.billingNote ? `, ${set.billingNote}` : ''}`}
                    >
                      <span className="name" style={{ fontSize: nameSize }}>
                        {name}
                      </span>
                      {showWhen && (
                        <span className="when">
                          {block && !full
                            ? `${fmtTime(block.startTs)}-${fmtTime(block.endTs)}`
                            : `${fmtTime(set.startTs)}-${fmtTime(set.endTs)}`}
                        </span>
                      )}
                      {here.length > 0 && (
                        <span className="marks">
                          {here.map((m) => (
                            <span key={m.userId} className="mark" style={{ background: m.color }} title={m.displayName} />
                          ))}
                        </span>
                      )}
                    </div>
                  );
                })}
            </div>
          ))}
        </div>
      </div>
    </div>
    </>
  );
}

export function GridLegend() {
  return (
    <div className="legend">
      <span>
        <span className="swatch" style={{ background: '#1d2a26', borderLeft: '3px solid #5fa860' }} />
        picked
      </span>
      <span>
        <span className="swatch" style={{ background: '#262218', borderLeft: '3px solid #c8a33a' }} />
        part of it only
      </span>
      <span>Click a set to add it or take it off.</span>
    </div>
  );
}
